import { WORK_MODEL_LABELS, type WorkModel } from "./types/job";

/** Suggestions start at two characters ("Au" is a town) and after a short
 * pause in typing, so the provider gets one request per pause (#49). */
export const PLACE_QUERY_MIN_CHARS = 2;
export const PLACE_QUERY_DEBOUNCE_MS = 250;

/** Where the Standort text came from: typed now, saved on the job before,
 * or filled in by the job import. */
export type StandortOrigin = "typed" | "saved" | "imported";

export interface StandortState {
  text: string;
  /** A suggestion was picked, or the saved job already has coordinates. */
  confirmed: boolean;
  origin: StandortOrigin;
  workModel: WorkModel | null;
  /** The recruiter has left the field since typing. */
  blurred: boolean;
  /** The region of the place just picked: two towns can share a name. */
  pickedRegion?: string | null;
}

export interface StandortMessage {
  tone: "success" | "warning" | "hint";
  text: string;
}

/** What the Standort field says under itself, in one line so it doesn't push
 * the fields below (#186). An unconfirmed place is allowed; it never blocks. */
export function standortMessage(s: StandortState): StandortMessage | null {
  if (s.confirmed) {
    return {
      tone: "success",
      text: s.pickedRegion ? `Ort erkannt · ${s.pickedRegion}` : "Ort erkannt",
    };
  }
  if (s.text.trim() && s.origin === "imported") {
    return {
      tone: "warning",
      text: "Aus dem Import – bitte Ort aus der Liste bestätigen.",
    };
  }
  // Remote only drops the distance warnings: no radius is used anyway.
  if (s.workModel === "remote") {
    return {
      tone: "hint",
      text: "Remote: Firmensitz oder Region, kein Umkreis.",
    };
  }
  if (!s.text.trim()) return null;
  if (s.origin === "saved") {
    return {
      tone: "warning",
      text: "Ort nicht bestätigt – bitte aus der Liste wählen.",
    };
  }
  if (!s.blurred) return null;
  return {
    tone: "warning",
    text: "Ort nicht erkannt – bitte aus der Liste wählen.",
  };
}

// Germany is implied on Vidario's own screens; Austria and Switzerland aren't.
const IMPLIED_COUNTRY = /,\s*(Deutschland|Germany)\s*$/i;

/** A job's place: "Düsseldorf, Deutschland · Hybrid", or with `short` (cards,
 * table, Longlist header — #186) "Düsseldorf · Hybrid". The client PDF keeps it full. */
export function jobPlaceLine(
  location: string,
  workModel: WorkModel | null,
  { short = false }: { short?: boolean } = {},
): string {
  const model = workModel ? WORK_MODEL_LABELS[workModel] : "";
  const place = short ? location.replace(IMPLIED_COUNTRY, "") : location;
  return [place.trim(), model].filter(Boolean).join(" · ");
}

export function placeQueryReady(text: string): boolean {
  return text.trim().length >= PLACE_QUERY_MIN_CHARS;
}
